import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  ScaleFade,
  Badge,
  Grid,
  GridItem,

  Icon,
  Stack,
  Divider,
  Progress,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaLayerGroup, 
  FaCube, 
  FaRandom, 
  FaGem, 
  FaRocket,
  FaPalette,
  FaCogs,
  FaFileCode,
  FaMagic,
  FaDna,
  FaChartLine,
  FaShieldAlt,
  FaInfinity,

  FaStar,
} from 'react-icons/fa';
import useAuth from '../store/useAuth';
import { COLORS, ROUTES } from '../utils/constants';

// Motion components
const MotionBox = motion(Box);
const MotionButton = motion(Button);
const MotionText = motion(Text);

// Floating animation keyframes
const floatAnimation = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  33% { transform: translateY(-10px) rotate(1deg); }
  66% { transform: translateY(5px) rotate(-1deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const glowPulse = keyframes`
  0% { box-shadow: 0 0 20px rgba(30, 220, 11, 0.4); }
  50% { box-shadow: 0 0 40px rgba(30, 220, 11, 0.6), 0 0 60px rgba(30, 220, 11, 0.3); }
  100% { box-shadow: 0 0 20px rgba(30, 220, 11, 0.4); }
`;

// Animated background component
const AnimatedBackground = () => (
  <Box
    position="absolute"
    top={0}
    left={0}
    right={0}
    bottom={0}
    overflow="hidden"
    zIndex={0}
  >
    {/* Grid pattern */}
    <Box
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      backgroundImage={`
        linear-gradient(rgba(30, 220, 11, 0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(30, 220, 11, 0.03) 1px, transparent 1px)
      `}
      backgroundSize="50px 50px"
      opacity={0.5}
    />
    
    {/* Floating orbs */}
    {[...Array(5)].map((_, i) => (
      <MotionBox
        key={i}
        position="absolute"
        width={`${100 + i * 50}px`}
        height={`${100 + i * 50}px`}
        borderRadius="full"
        background={`radial-gradient(circle, rgba(30, 220, 11, ${0.2 - i * 0.03}) 0%, transparent 70%)`}
        initial={{
          x: Math.random() * window.innerWidth,
          y: Math.random() * window.innerHeight,
        }}
        animate={{
          x: [null, Math.random() * window.innerWidth],
          y: [null, Math.random() * window.innerHeight],
        }}
        transition={{
          duration: 20 + i * 5,
          repeat: Infinity,
          repeatType: "reverse",
          ease: "linear",
        }}
      />
    ))}
  </Box>
);

// Enhanced feature card with better animations
const FeatureCard = ({ icon, title, description, delay = 0, stats, badge }) => (
  <MotionBox
    initial={{ opacity: 0, y: 50, rotateX: -30 }}
    animate={{ opacity: 1, y: 0, rotateX: 0 }}
    transition={{ duration: 0.8, delay, type: "spring", stiffness: 100 }}
    whileHover={{ 
      y: -10,
      transition: { duration: 0.3 }
    }}
    h="100%"
  >
    <Box
      p={8}
      bg="linear-gradient(135deg, rgba(0, 0, 0, 0.8) 0%, rgba(30, 220, 11, 0.05) 100%)"
      borderRadius="2xl"
      borderWidth="1px"
      borderColor="rgba(30, 220, 11, 0.3)"
      h="100%"
      position="relative"
      overflow="hidden"
      transition="all 0.4s"
      cursor="pointer"
      role="group"
      _hover={{
        borderColor: COLORS.primary,
        transform: "translateZ(10px)",
        boxShadow: `
          0 20px 40px rgba(30, 220, 11, 0.3),
          inset 0 0 30px rgba(30, 220, 11, 0.1)
        `,
      }}
    >
      {/* Animated background gradient */}
      <Box
        position="absolute"
        top="-100%"
        left="-100%"
        width="300%"
        height="300%"
        background="radial-gradient(circle, rgba(30, 220, 11, 0.1) 0%, transparent 50%)"
        opacity={0}
        transition="all 0.6s"
        _groupHover={{ 
          opacity: 1,
          transform: "rotate(45deg)"
        }}
      />
      
      <VStack spacing={6} align="start" position="relative" zIndex={1}>
        <HStack justify="space-between" w="100%">
          <MotionBox
            p={4}
            bg="rgba(30, 220, 11, 0.1)"
            borderRadius="xl"
            borderWidth="1px"
            borderColor="rgba(30, 220, 11, 0.3)"
            animation={`${floatAnimation} 4s ease-in-out infinite`}
            position="relative"
          >
            <Icon as={icon} boxSize={8} color={COLORS.primary} />
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              borderRadius="xl"
              opacity={0}
              transition="opacity 0.3s"
              _groupHover={{ 
                opacity: 1,
                boxShadow: `0 0 30px rgba(30, 220, 11, 0.6)`
              }}
            />
          </MotionBox>
          
          {badge && (
            <Badge
              bg="rgba(30, 220, 11, 0.2)"
              color={COLORS.primary}
              px={3}
              py={1}
              borderRadius="full"
              fontSize="xs"
              fontWeight="bold"
              textTransform="uppercase"
            >
              {badge}
            </Badge>
          )}
        </HStack>
        
        <Box>
          <Text
            fontSize="2xl"
            fontWeight="bold"
            color={COLORS.text}
            mb={2}
            transition="all 0.3s"
            _groupHover={{ 
              color: COLORS.primary,
              textShadow: `0 0 20px rgba(30, 220, 11, 0.5)`
            }}
          >
            {title}
          </Text>
          
          <Text
            fontSize="md"
            color={COLORS.textSecondary}
            lineHeight="tall"
          >
            {description}
          </Text>
        </Box>

        {stats && (
          <HStack spacing={8} pt={4} borderTop="1px solid rgba(30, 220, 11, 0.1)" w="100%">
            {stats.map((stat, index) => (
              <Box key={index}>
                <Text fontSize="xs" color={COLORS.textSecondary} mb={1}>
                  {stat.label}
                </Text>
                <Text fontSize="lg" fontWeight="bold" color={COLORS.primary}>
                  {stat.value}
                </Text>
              </Box>
            ))}
          </HStack>
        )}
      </VStack>
    </Box>
  </MotionBox>
);

// Workflow step with enhanced design
const WorkflowStep = ({ number, icon, title, description, delay = 0, isLast = false }) => (
  <MotionBox
    initial={{ opacity: 0, x: -50 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.8, delay, type: "spring" }}
  >
    <HStack spacing={6} align="stretch" position="relative">
      {/* Connector line */}
      {!isLast && (
        <Box
          position="absolute"
          left="35px"
          top="70px"
          bottom="-70px"
          width="2px"
          bg="linear-gradient(to bottom, rgba(30, 220, 11, 0.4), rgba(30, 220, 11, 0.1))"
        />
      )}
      
      {/* Step number */}
      <MotionBox
        minW="70px"
        h="70px"
        borderRadius="full"
        bg="linear-gradient(135deg, #1EDC0B 0%, #18B909 100%)"
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontWeight="bold"
        fontSize="2xl"
        color={COLORS.background}
        position="relative"
        animation={`${glowPulse} 3s ease-in-out infinite`}
        whileHover={{ scale: 1.1 }}
      >
        <Icon as={icon} boxSize={7} />
        <Badge
          position="absolute"
          top={-2}
          right={-2}
          bg={COLORS.background}
          color={COLORS.primary}
          borderWidth="2px"
          borderColor={COLORS.primary}
          borderRadius="full"
          fontSize="xs"
          px={2}
        >
          {number}
        </Badge>
      </MotionBox>
      
      <Box
        flex={1}
        p={6}
        bg="rgba(0, 0, 0, 0.4)"
        borderRadius="xl"
        borderWidth="1px"
        borderColor="rgba(30, 220, 11, 0.2)"
        transition="all 0.3s"
        _hover={{
          borderColor: COLORS.primary,
          bg: "rgba(30, 220, 11, 0.05)",
          transform: "translateX(10px)"
        }}
      >
        <Text
          fontSize="xl"
          fontWeight="bold"
          color={COLORS.text}
          mb={2}
        >
          {title}
        </Text>
        
        <Text
          fontSize="md"
          color={COLORS.textSecondary}
          lineHeight="tall"
        >
          {description}
        </Text>
      </Box>
    </HStack>
  </MotionBox>
);

// Preview showcase component
const PreviewShowcase = () => {
  const [activeTab, setActiveTab] = useState(0);
  const tabs = [
    { label: "Trait Layers", icon: FaLayerGroup },
    { label: "Combinations", icon: FaRandom },
    { label: "Metadata", icon: FaFileCode },
  ];

  return (
    <MotionBox
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8, delay: 0.5 }}
    >
      <Box
        bg="rgba(0, 0, 0, 0.6)"
        borderRadius="2xl"
        borderWidth="1px"
        borderColor="rgba(30, 220, 11, 0.3)"
        overflow="hidden"
        p={8}
      >
        {/* Tab headers */}
        <HStack spacing={4} mb={6}>
          {tabs.map((tab, index) => (
            <Button
              key={index}
              variant="ghost"
              leftIcon={<Icon as={tab.icon} />}
              onClick={() => setActiveTab(index)}
              color={activeTab === index ? COLORS.primary : COLORS.textSecondary}
              borderBottomWidth={2}
              borderBottomColor={activeTab === index ? COLORS.primary : "transparent"}
              borderRadius={0}
              _hover={{ color: COLORS.primary }}
              transition="all 0.3s"
            >
              {tab.label}
            </Button>
          ))}
        </HStack>

        {/* Tab content */}
        <AnimatePresence mode="wait">
          {activeTab === 0 && (
            <MotionBox
              key="layers"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                {["Background", "Character", "Clothing", "Accessories", "Effects", "Rare Traits", "Legendary", "Special"].map((layer, i) => (
                  <Box
                    key={i}
                    h="100px"
                    bg="rgba(30, 220, 11, 0.05)"
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor="rgba(30, 220, 11, 0.2)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    position="relative"
                    overflow="hidden"
                    transition="all 0.3s"
                    _hover={{
                      borderColor: COLORS.primary,
                      transform: "scale(1.05)"
                    }}
                  >
                    <Text color={COLORS.text} fontWeight="medium">
                      {layer}
                    </Text>
                    <Box
                      position="absolute"
                      top={0}
                      right={0}
                      bg={COLORS.primary}
                      color={COLORS.background}
                      px={2}
                      py={1}
                      fontSize="xs"
                      fontWeight="bold"
                    >
                      {Math.floor(Math.random() * 50 + 10)}
                    </Box>
                  </Box>
                ))}
              </SimpleGrid>
            </MotionBox>
          )}

          {activeTab === 1 && (
            <MotionBox
              key="combinations"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Grid templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }} gap={{ base: 3, md: 4 }}>
                {[...Array(6)].map((_, i) => (
                  <Box
                    key={i}
                    h="150px"
                    bg={`linear-gradient(135deg, 
                      rgba(30, 220, 11, ${0.1 + i * 0.02}) 0%, 
                      rgba(0, 0, 0, 0.8) 100%
                    )`}
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor="rgba(30, 220, 11, 0.3)"
                    p={4}
                    position="relative"
                    overflow="hidden"
                  >
                    <VStack align="start" spacing={2}>
                      <Badge colorScheme="green" variant="subtle">
                        #{1000 + i}
                      </Badge>
                      <Text fontSize="sm" color={COLORS.textSecondary}>
                        Rarity: {["Common", "Uncommon", "Rare", "Epic", "Legendary", "Mythic"][i]}
                      </Text>
                    </VStack>
                    <Icon
                      as={FaGem}
                      position="absolute"
                      bottom={2}
                      right={2}
                      boxSize={6}
                      color={COLORS.primary}
                      opacity={0.3}
                    />
                  </Box>
                ))}
              </Grid>
            </MotionBox>
          )}

          {activeTab === 2 && (
            <MotionBox
              key="metadata"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Box
                bg="rgba(0, 0, 0, 0.6)"
                borderRadius="lg"
                p={4}
                fontFamily="monospace"
                fontSize="sm"
                color={COLORS.primary}
                overflowX="auto"
              >
                <pre>{JSON.stringify({
                  name: "NFT #1337",
                  description: "AI-generated unique NFT",
                  image: "ipfs://...",
                  attributes: [
                    { trait_type: "Background", value: "Cosmic" },
                    { trait_type: "Character", value: "Cyborg" },
                    { trait_type: "Rarity", value: "Legendary" }
                  ]
                }, null, 2)}</pre>
              </Box>
            </MotionBox>
          )}
        </AnimatePresence>
      </Box>
    </MotionBox>
  );
};

function NFTMaker() {
  const [isVisible, setIsVisible] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  
  const navigate = useNavigate();
  const { user, isLoggedIn } = useAuth();

  // Animation on mount
  useEffect(() => {
    setIsVisible(true);
    // Simulate loading progress
    const interval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 20;
      });
    }, 200);
    return () => clearInterval(interval);
  }, []);

  // Redirect to login if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
    }
  }, [isLoggedIn, navigate]);



  const features = [
    {
      icon: FaDna,
      title: "Trait Combination System",
      description: "Upload base images and trait variations. The system intelligently combines them to create unique NFTs while preventing duplicates and ensuring visual consistency.",
      stats: [
        { label: "Max Traits", value: "20+" },
        { label: "Auto-Check", value: "100%" }
      ],
      badge: "Core",
      delay: 0.1
    },
    {
      icon: FaMagic,
      title: "Automatic Rarity Distribution",
      description: "Define rarity percentages for each trait. The generator automatically balances distribution to match your specifications across the entire collection.",
      stats: [
        { label: "Rarity Control", value: "Precise" },
        { label: "Balance Check", value: "Auto" }
      ],
      badge: "Smart",
      delay: 0.2
    },
    {
      icon: FaChartLine,
      title: "Metadata Generation",
      description: "Every NFT gets complete JSON metadata including trait properties, rarity scores, and blockchain-ready format for seamless marketplace integration.",
      stats: [
        { label: "Format", value: "JSON" },
        { label: "Standards", value: "ERC-721" }
      ],
      badge: "Complete",
      delay: 0.3
    }
  ];

  const workflowSteps = [
    {
      number: 1,
      icon: FaPalette,
      title: "Upload Base Assets",
      description: "Upload your base artwork and trait variations. Organize them by categories like backgrounds, characters, accessories, etc.",
      delay: 0.4
    },
    {
      number: 2,
      icon: FaCogs,
      title: "Configure Traits & Rarity",
      description: "Set rarity percentages for each trait and define compatibility rules. Specify which traits can appear together.",
      delay: 0.5
    },
    {
      number: 3,
      icon: FaInfinity,
      title: "Generate Collection",
      description: "Choose your collection size (100-10,000 NFTs) and click generate. The system creates unique combinations based on your settings.",
      delay: 0.6
    },
    {
      number: 4,
      icon: FaRocket,
      title: "Export & Deploy",
      description: "Download your complete collection with images, metadata files, and rarity reports. Ready for blockchain deployment.",
      delay: 0.7
    }
  ];

  const stats = [
    { label: "Max Collection Size", value: "10K", icon: FaCube },
    { label: "Processing Speed", value: "100/min", icon: FaRocket },
    { label: "Trait Variations", value: "50+", icon: FaGem },
    { label: "Unique Guarantee", value: "100%", icon: FaStar }
  ];

  return (
    <Box minH="100vh" bg={COLORS.background} color={COLORS.text} position="relative">
      <AnimatedBackground />
      
      <Container maxW="container.xl" py={{ base: 4, md: 8 }} px={{ base: 4, md: 6 }} position="relative" zIndex={1}>
        <VStack spacing={{ base: 12, md: 20 }} align="stretch">
          
          {/* Loading progress */}
          <Progress
            value={loadingProgress}
            size="xs"
            colorScheme="green"
            position="fixed"
            top={0}
            left={0}
            right={0}
            zIndex={100}
            isAnimated
          />
          
          {/* Hero Section */}
          <ScaleFade initialScale={0.8} in={isVisible}>
                          <MotionBox
                initial={{ opacity: 0, y: 100 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.2 }}
                textAlign="center"
                py={{ base: 8, md: 16 }}
              >
                <VStack spacing={{ base: 6, md: 8 }}>
                  {/* Animated Icon */}
                  <MotionBox
                    animate={{ 
                      rotate: [0, 360],
                      scale: [1, 1.2, 1]
                    }}
                    transition={{ 
                      duration: 20,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                  >
                    <Icon 
                      as={FaGem} 
                      boxSize={{ base: 16, md: 20 }}
                      color={COLORS.primary}
                      filter="drop-shadow(0 0 30px rgba(30, 220, 11, 0.6))"
                    />
                  </MotionBox>
                  
                  {/* Title with gradient */}
                  <VStack spacing={4}>
                    <Stack direction={{ base: 'column', sm: 'row' }} spacing={4} align="center" justify="center">
                      <Heading
                        size={{ base: '2xl', md: '4xl' }}
                        bgGradient="linear(to-r, #1EDC0B, #FFFFFF, #1EDC0B)"
                        bgClip="text"
                        fontWeight="extrabold"
                        letterSpacing="tight"
                        textShadow={`0 0 40px rgba(30, 220, 11, 0.5)`}
                      >
                        NFT Maker
                      </Heading>
                      <MotionBox
                        animate={{ 
                          y: [-5, 5, -5],
                          rotate: [-5, 5, -5]
                        }}
                        transition={{ 
                          duration: 3,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      >
                        <Badge
                          colorScheme="green"
                          variant="solid"
                          px={{ base: 3, md: 4 }}
                          py={{ base: 1, md: 2 }}
                          borderRadius="full"
                          fontSize={{ base: 'sm', md: 'md' }}
                          fontWeight="bold"
                          bg={COLORS.primary}
                          color={COLORS.background}
                          boxShadow={`0 0 30px rgba(30, 220, 11, 0.6)`}
                        >
                          COMING SOON
                        </Badge>
                      </MotionBox>
                    </Stack>
                    
                    <Text
                      fontSize={{ base: 'lg', md: '2xl' }}
                      color={COLORS.textSecondary}
                      maxW="800px"
                      lineHeight="tall"
                      fontWeight="medium"
                      px={{ base: 4, md: 0 }}
                    >
                      Upload your base artwork, define traits and variations, then generate thousands of unique NFTs. 
                      The system automatically handles rarity distribution, metadata creation, and ensures every NFT is unique.
                    </Text>
                  </VStack>



                {/* Stats */}
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8} pt={12} w="100%">
                  {stats.map((stat, index) => (
                    <MotionBox
                      key={index}
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                    >
                      <Stat textAlign="center">
                        <StatLabel color={COLORS.textSecondary} fontSize="sm">
                          <Icon as={stat.icon} mr={2} />
                          {stat.label}
                        </StatLabel>
                        <StatNumber 
                          color={COLORS.primary} 
                          fontSize="3xl"
                          fontWeight="bold"
                        >
                          {stat.value}
                        </StatNumber>
                      </Stat>
                    </MotionBox>
                  ))}
                </SimpleGrid>
              </VStack>
            </MotionBox>
          </ScaleFade>

          <Divider borderColor="rgba(30, 220, 11, 0.2)" />

          {/* Preview Showcase */}
          <VStack spacing={12}>
            <MotionText
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              fontSize="3xl"
              fontWeight="bold"
              textAlign="center"
              bgGradient="linear(to-r, #FFFFFF, #1EDC0B)"
              bgClip="text"
            >
              See It In Action
            </MotionText>
            
            <PreviewShowcase />
          </VStack>

          <Divider borderColor="rgba(30, 220, 11, 0.2)" />

          {/* Features Section */}
          <VStack spacing={12}>
            <MotionText
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              fontSize="3xl"
              fontWeight="bold"
              textAlign="center"
              bgGradient="linear(to-r, #FFFFFF, #1EDC0B)"
              bgClip="text"
            >
              How It Works
            </MotionText>
            
            <Grid
              templateColumns={{ base: "1fr", lg: "repeat(3, 1fr)" }}
              gap={8}
              w="100%"
            >
              {features.map((feature, index) => (
                <GridItem key={index}>
                  <FeatureCard {...feature} />
                </GridItem>
              ))}
            </Grid>
          </VStack>

          <Divider borderColor="rgba(30, 220, 11, 0.2)" />

          {/* Workflow Section */}
          <VStack spacing={12}>
            <MotionText
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              fontSize="3xl"
              fontWeight="bold"
              textAlign="center"
              bgGradient="linear(to-r, #1EDC0B, #FFFFFF)"
              bgClip="text"
            >
              Simple 4-Step Process
            </MotionText>
            
            <VStack spacing={12} maxW="900px" mx="auto" w="100%">
              {workflowSteps.map((step, index) => (
                <WorkflowStep 
                  key={index} 
                  {...step} 
                  isLast={index === workflowSteps.length - 1}
                />
              ))}
            </VStack>
          </VStack>



        </VStack>
      </Container>


    </Box>
  );
}

export default NFTMaker; 